// Centralized date helpers
// UI/Inputs must use format: YYYY-MM-DD HH:MM (no seconds, no timezone)
// Convention: Interpret the string as LOCAL time when converting to Date.
// For API submission, convert to ISO 8601 UTC string if backend expects ISO.

// Pads a number to 2 digits
const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));

export const isValidYmdHm = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  // Strict pattern YYYY-MM-DD HH:MM
  const re = /^(\d{4})-(\d{2})-(\d{2})\s(\d{2}):(\d{2})$/;
  const m = value.match(re);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const h = Number(m[4]);
  const mi = Number(m[5]);
  if (mo < 1 || mo > 12) return false;
  if (h < 0 || h > 23) return false;
  if (mi < 0 || mi > 59) return false;
  const daysInMonth = new Date(y, mo, 0).getDate(); // mo is 1-based
  if (d < 1 || d > daysInMonth) return false;
  return true;
};

export const parseYmdHmToDate = (value: string): Date => {
  if (!isValidYmdHm(value)) throw new Error('Invalid YYYY-MM-DD HH:MM');
  const [datePart, timePart] = value.split(' ');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  // Interpret as local time
  return new Date(y, m - 1, d, hh, mm, 0, 0);
};

export const formatYmdHm = (value?: string | number | Date | null): string => {
  if (value == null || value === '') return '-';
  try {
    let d: Date;
    if (typeof value === 'string') {
      const maybe = tryParseToDate(value);
      if (!maybe) return value;
      d = maybe;
    } else {
      d = new Date(value as any);
    }
    if (isNaN(d.getTime())) return String(value);
    const y = d.getFullYear();
    const mo = pad2(d.getMonth() + 1);
    const day = pad2(d.getDate());
    const hh = pad2(d.getHours());
    const mm = pad2(d.getMinutes());
    return `${y}-${mo}-${day} ${hh}:${mm}`;
  } catch (_) {
    return String(value);
  }
};

export const toIsoUtcFromYmdHm = (value: string): string => {
  const d = parseYmdHmToDate(value);
  // Convert local time to ISO UTC (Z)
  return d.toISOString();
};

// Flexible parsing used where we may get ISO from API or UI format from inputs
export const isValidIso8601 = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  const re = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{3})?)?(Z|[+-]\d{2}:?\d{2})?$/;
  if (!re.test(value)) return false;
  const t = Date.parse(value);
  return Number.isFinite(t);
};

export const tryParseToDate = (value: string): Date | null => {
  try {
    if (isValidYmdHm(value)) return parseYmdHmToDate(value);
    if (isValidIso8601(value)) return new Date(value);
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

// Backward-compat: keep the previous ISO helpers (used in history)
export const toUtcIsoSeconds = (value: string | number | Date): string => {
  const d = new Date(value as any);
  if (isNaN(d.getTime())) throw new Error('Invalid date');
  return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
};
export const formatIso8601 = (value?: string | number | Date | null): string => {
  if (value == null || value === '') return '-';
  try {
    const d = new Date(value as any);
    if (isNaN(d.getTime())) return String(value);
    return toUtcIsoSeconds(d);
  } catch (_) {
    return String(value);
  }
};
export const normalizeInputToIsoUtc = (value: string): string => {
  if (isValidIso8601(value)) return toUtcIsoSeconds(value);
  const d = new Date(value);
  if (!isNaN(d.getTime())) return toUtcIsoSeconds(d);
  throw new Error('Invalid ISO 8601 date');
};
